// Persistence for the emulated CPU state.
//
// We keep two copies of the save:
//   1. IndexedDB ("TamaDB") — primary store.
//   2. localStorage ("tamaState") — a mirror that tends to survive the storage
//      eviction that hits home-screen PWAs on iOS Safari (which silently clears
//      IndexedDB after a period of disuse). On restore we take whichever copy
//      is present, preferring the newer one.
//
// Each save also records `savedAt` (wall-clock ms) so that on the next load we
// can fast-forward the emulator by the real time that elapsed while the page
// was closed (see worker.ts catchUp()).

export interface SavedState {
  buffer: Uint32Array;
  savedAt: number;
}

const LS_BUFFER_KEY = "tamaState";
const LS_SAVED_AT_KEY = "tamaSavedAt";

export async function saveWasmStateToIndexedDB(
  cpuState: number[],
  savedAt: number
) {
  const memory = new Uint32Array(cpuState);

  const db: IDBDatabase = await new Promise((resolve, reject) => {
    const request = indexedDB.open("TamaDB", 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore("states", { keyPath: "id" });
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = (ev: unknown) => {
      console.error("DB save error on open", ev);
      reject(request.error ?? new Error("Unknown DB error"));
    };
  });

  await new Promise((resolve, reject) => {
    const transaction = db.transaction(["states"], "readwrite");
    const store = transaction.objectStore("states");
    const request = store.put({ id: "tamaState", buffer: memory, savedAt });
    request.onsuccess = () => {
      resolve(undefined);
    };
    request.onerror = (ev: unknown) => {
      console.error("DB save error on write", ev);
      reject(request.error ?? new Error("Unknown DB error"));
    };
  });

  db.close();
}

export async function readWasmStateFromIndexedDB(): Promise<
  SavedState | undefined
> {
  const db: IDBDatabase = await new Promise((resolve, reject) => {
    const request = indexedDB.open("TamaDB", 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore("states", { keyPath: "id" });
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error("Unknown DB error"));
    };
  });

  const state: SavedState | undefined = await new Promise(
    (resolve, reject) => {
      const transaction = db.transaction(["states"], "readonly");
      const store = transaction.objectStore("states");
      const request = store.get("tamaState");
      request.onsuccess = () => {
        const result = request.result as
          | { buffer?: Uint32Array; savedAt?: number }
          | undefined;
        // NOTE: always resolve — a fresh DB has no saved state and must not
        // leave this promise hanging (the original code only resolved inside
        // the `if`, so first-ever loads would hang forever).
        if (result != null && result.buffer instanceof Uint32Array) {
          resolve({
            buffer: result.buffer,
            savedAt: typeof result.savedAt === "number" ? result.savedAt : 0,
          });
        } else {
          resolve(undefined);
        }
      };
      request.onerror = () => {
        reject(request.error ?? new Error("Unknown DB error"));
      };
    }
  );

  db.close();

  return state;
}

export function saveWasmStateToLocalStorage(
  cpuState: number[],
  savedAt: number
) {
  try {
    localStorage.setItem(LS_BUFFER_KEY, JSON.stringify(cpuState));
    localStorage.setItem(LS_SAVED_AT_KEY, String(savedAt));
  } catch (e) {
    // Storage full / disabled (private mode). IndexedDB is still the primary
    // store, so a localStorage failure is non-fatal.
    console.error("Error saving to localStorage: ", e);
  }
}

export function readWasmStateFromLocalStorage(): SavedState | undefined {
  try {
    const raw = localStorage.getItem(LS_BUFFER_KEY);
    if (raw == null) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return undefined;
    }
    const savedAtRaw = localStorage.getItem(LS_SAVED_AT_KEY);
    const savedAt = savedAtRaw != null ? Number(savedAtRaw) : 0;
    return {
      buffer: new Uint32Array(parsed as number[]),
      savedAt: Number.isFinite(savedAt) ? savedAt : 0,
    };
  } catch (e) {
    console.error("Error reading from localStorage: ", e);
    return undefined;
  }
}
